/**
 * The workspaces I've opened this session stay warm, like Slack's switcher: their view models (the cache, the
 * mailbox, the tabs) are kept, so switching back paints at once and then catches up. Only the one in front is live
 * (its socket, notifications and the app-icon badge); the others are suspended. Past `limit` the least recently used
 * one is disposed, and signing out (or in as someone else) disposes them all.
 */

export type WarmEntry = {
  /** Another workspace came to the front: close the socket, stop notifying. */
  suspend: () => void;
  /** Back in front: reconnect (replaying what was missed) and refresh. */
  resume: () => void;
  dispose: () => void;
};

/** How many workspaces stay warm (the one in front included). */
export const WARM_LIMIT = 4;

export class WarmWorkspaces<T extends WarmEntry> {
  /** Least recently used first. */
  private readonly entries = new Map<string, T>();
  private front: string | undefined;
  private pendingSuspend: { key: string; timer: ReturnType<typeof setTimeout> } | undefined;

  constructor(private readonly limit = WARM_LIMIT) {}

  /** The kept view models for `key`, made on first use (during render: no other side effects). */
  obtain(key: string, create: () => T): T {
    let value = this.entries.get(key);
    if (value === undefined) {
      value = create();
      this.entries.set(key, value);
    }
    return value;
  }

  /**
   * `key` is on screen: the one that was in front is suspended and this one resumes. Idempotent, so StrictMode's
   * mount/unmount/mount and a remount of the same workspace don't reconnect.
   */
  activate(key: string): void {
    const value = this.entries.get(key);
    if (value === undefined) return;
    if (this.pendingSuspend?.key === key) {
      clearTimeout(this.pendingSuspend.timer);
      this.pendingSuspend = undefined;
    } else this.flushSuspend();
    // Most recently used last.
    this.entries.delete(key);
    this.entries.set(key, value);
    if (this.front !== key) {
      const prev = this.front === undefined ? undefined : this.entries.get(this.front);
      prev?.suspend();
      this.front = key;
      value.resume();
    }
    this.trim();
  }

  /** `key` left the screen: suspended after the current task, unless it comes straight back (StrictMode). */
  deactivate(key: string): void {
    if (this.front !== key) return;
    this.flushSuspend();
    const timer = setTimeout(() => {
      if (this.pendingSuspend?.timer !== timer) return;
      this.pendingSuspend = undefined;
      this.suspendFront(key);
    }, 0);
    this.pendingSuspend = { key, timer };
  }

  /** Disposes every kept workspace (sign-out, another person). */
  clear(): void {
    if (this.pendingSuspend !== undefined) clearTimeout(this.pendingSuspend.timer);
    this.pendingSuspend = undefined;
    this.front = undefined;
    const all = [...this.entries.values()];
    this.entries.clear();
    for (const v of all) v.dispose();
  }

  /** Disposes the kept workspaces whose key fails `keep` (e.g. ones I've left). The one in front stays. */
  retain(keep: (key: string) => boolean): void {
    for (const [key, v] of this.entries) {
      if (key === this.front || keep(key)) continue;
      this.entries.delete(key);
      v.dispose();
    }
  }

  get size(): number {
    return this.entries.size;
  }

  has(key: string): boolean {
    return this.entries.has(key);
  }

  private flushSuspend(): void {
    const p = this.pendingSuspend;
    if (p === undefined) return;
    clearTimeout(p.timer);
    this.pendingSuspend = undefined;
    this.suspendFront(p.key);
  }

  private suspendFront(key: string): void {
    if (this.front !== key) return;
    this.front = undefined;
    this.entries.get(key)?.suspend();
  }

  private trim(): void {
    for (const [key, v] of this.entries) {
      if (this.entries.size <= this.limit) return;
      if (key === this.front) continue;
      this.entries.delete(key);
      v.dispose();
    }
  }
}
